import { ChangeDetectionStrategy, Component, computed, inject, output, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { EnpsComponent } from '@common/components/base/enps-component';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { NavigationService } from '@common/services/navigation-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { Store } from '@ngrx/store';
import { PSTWEB_SUITE_NAV, PSTWEB_VIEW } from '@src/suite/common/enums/constants';
import { AppName } from '@src/suite/common/enums/enums';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import {
    selectCurrentApplication,
    selectIsManagePublicationChannels,
    selectIsManageTeamMembers,
    selectIsPlanningViews,
    selectIsTemplates,
    selectIsWastebin,
} from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { selectCurrentViewState, selectViewHasFilter } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { AppService } from '@suite/app.service';

import { SearchBoxComponent } from '../../../common/components/search-box/search-box';
import { TopBarUserMenuComponent } from '../top-bar-user-menu/top-bar-user-menu';

@Component({
    selector: 'st-top-bar',
    imports: [MatIcon, MatIconButton, MatToolbar, MatToolbarRow, MatTooltip, SearchBoxComponent, TopBarUserMenuComponent],
    templateUrl: './top-bar.html',
    styleUrl: './top-bar.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-top-bar',
    },
})
export class TopBarComponent extends EnpsComponent {
    appService = inject(AppService);
    nomWebServicesJson = inject(NomWebServicesJson);
    navigationService = inject(NavigationService);
    visualStateService = inject(VisualStateService);
    globalTablesService = inject(GlobalTablesService);
    private store = inject(Store);

    readonly toggleFilter = output();
    readonly toggleSideBar = output();

    vs = this.store.selectSignal(selectCurrentViewState);
    application = this.store.selectSignal(selectCurrentApplication);
    languageSettings = this.store.selectSignal(selectI18N);
    isWastebinOpen = this.store.selectSignal(selectIsWastebin);
    isTemplatesOpen = this.store.selectSignal(selectIsTemplates);

    isPlanningViews = this.store.selectSignal(selectIsPlanningViews);
    isManageTeamMembers = this.store.selectSignal(selectIsManageTeamMembers);
    isManagePublicationChannels = this.store.selectSignal(selectIsManagePublicationChannels);

    readonly label = computed(() => {
        if (this.isWastebinOpen()) {
            return this.languageSettings().PSTWebRestoreContentTitle;
        } else if (this.isTemplatesOpen()) {
            return this.languageSettings().PSTWebManageTemplatesTitle;
        } else if (this.vs() && this.application() !== AppName.EMBEDDED) {
            const translatedViewState = this.languageSettings()[`${PSTWEB_VIEW}_${this.vs().Path?.fullPath}_${this.vs().Title}`] ?? this.vs()?.Title;
            if (this.visualStateService.isLessThanXSmall()) {
                return translatedViewState;
            } else {
                const translatedApp =
                    this.languageSettings()[`${PSTWEB_SUITE_NAV}_${this.globalTablesService?.StorytellingNavigationDict?.[this.appService.applicationName]?.ID}`] ??
                    this.globalTablesService?.StorytellingNavigationDict?.[this.appService.applicationName]?.Name;
                return `${translatedApp} / ${translatedViewState}`;
            }
        } else {
            if (this.isManageTeamMembers()) {
                return this.languageSettings().PSTWebMTMLabel;
            }
            if (this.isManagePublicationChannels()) {
                return this.languageSettings().PSTWebMPCHLabel;
            }
            if (this.isPlanningViews()) {
                return this.languageSettings().PSTWebPlanningViewsLabel;
            }
            return this.languageSettings().PSTWebLoadingLabel;
        }
    });

    readonly isCurrentAppSearchEnabled = computed(() => this.appService?.applicationName !== AppName.CAST && this.appService?.applicationName !== AppName.EMBEDDED);

    readonly mobileSidebarToggleButtonVisible = computed(() => this.visualStateService.isLessThanSmall());

    viewStateHasFilter = this.store.selectSignal(selectViewHasFilter);

    readonly viewHasFilter = computed(() => this.viewStateHasFilter() || this.isManagePublicationChannels() || this.isManageTeamMembers() || this.isPlanningViews());
}
